import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import { INSTITUTE_STATUS, type InstituteStatus } from '../constants/institute.constants';

const BD_MOBILE_PATTERN = /^01[3-9]\d{8}$/;
const DIGITS_ONLY_PATTERN = /^\d+$/;

export const instituteFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name is required')
      .max(200, 'Name must be 200 characters or fewer'),
    nameBn: z.string().trim().max(200, 'Bangla name must be 200 characters or fewer'),
    description: z.string().trim().max(500, 'Description must be 500 characters or fewer'),
    instituteTypeId: z
      .number({ message: 'Institute type is required' })
      .int()
      .positive('Institute type is required'),
    examBoardId: z
      .number({ message: 'Exam board is required' })
      .int()
      .positive('Exam board is required'),
    geoUnitId: z.number({ message: 'Thana is required' }).int().positive('Thana is required'),
    eiinNumber: z
      .string()
      .trim()
      .refine((v) => v === '' || DIGITS_ONLY_PATTERN.test(v), {
        message: 'EIIN number must contain only digits',
      }),
    contactName: z
      .string()
      .trim()
      .min(1, 'Contact name is required')
      .max(100, 'Contact name must be 100 characters or fewer'),
    contactNumber: z.string().trim().regex(BD_MOBILE_PATTERN, 'Enter a valid number'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    isActive: z.boolean(),
  })
  .refine((data) => new Date(data.endDate) >= new Date(data.startDate), {
    path: ['endDate'],
    message: 'End date must be on or after the start date',
  });

export type InstituteFormValues = z.infer<typeof instituteFormSchema>;

export const instituteListItemSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  nameBn: z.string().nullable(),
  instituteTypeId: z.coerce.number().nullable(),
  instituteTypeName: z.string().nullable(),
  examBoardId: z.coerce.number().nullable(),
  examBoardName: z.string().nullable(),
  thanaId: z.coerce.number().nullable(),
  thanaName: z.string().nullable(),
  eiinNumber: z.string().nullable(),
  contactName: z.string().nullable(),
  contactNumber: z.string().nullable(),
  status: z.enum(INSTITUTE_STATUS),
  createdAt: z.coerce.date(),
});

export type InstituteListItem = z.infer<typeof instituteListItemSchema>;

export const paginatedInstitutesSchema = z.object({
  data: z.array(instituteListItemSchema),
  meta: paginationMetaSchema,
});

export type PaginatedInstitutes = z.infer<typeof paginatedInstitutesSchema>;

export interface InstituteListParams {
  instituteTypeId: number;
  districtId: number;
  examBoardId?: number | undefined;
  thanaId?: number | undefined;
  status?: InstituteStatus | undefined;
  eiinNumber?: string | undefined;
  name?: string | undefined;
}

export interface CreateInstitutePayload {
  name: string;
  nameBn?: string;
  description?: string;
  instituteTypeId: number;
  examBoardId: number;
  geoUnitId: number;
  eiinNumber?: string;
  contactName: string;
  contactNumber: string;
  startDate: string;
  endDate: string;
  isActive?: boolean;
}

export type UpdateInstitutePayload = CreateInstitutePayload;

const geoUnitRefSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
});

export const instituteDetailSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  nameBn: z.string().nullable(),
  description: z.string().nullable(),
  instituteTypeId: z.coerce.number().nullable(),
  examBoardId: z.coerce.number().nullable(),
  geo: z
    .object({
      thana: geoUnitRefSchema.nullable(),
      district: geoUnitRefSchema.nullable(),
      division: geoUnitRefSchema.nullable(),
    })
    .nullable(),
  eiinNumber: z.string().nullable(),
  contactName: z.string().nullable(),
  contactNumber: z.string().nullable(),
  startDate: z.coerce.date().nullable(),
  endDate: z.coerce.date().nullable(),
  status: z.enum(INSTITUTE_STATUS),
});

export type InstituteDetail = z.infer<typeof instituteDetailSchema>;
